import { useState, useEffect } from "react";
import axios from "axios";
import { Download, Eye } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api"
});

export default function FormsSupervisor() {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [sites, setSites] = useState([]);
  const [currentSite, setCurrentSite] = useState(null);
  const [expandedSub, setExpandedSub] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("ss_token");
    if (token) {
      API.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }

    loadSites();
  }, []);

  useEffect(() => {
    if (currentSite) {
      loadSubmissions();
    }
  }, [currentSite]);

  const loadSites = async () => {
    try {
      const response = await API.get("/sites/supervisor");
      setSites(response.data);
      if (response.data.length > 0) {
        setCurrentSite(response.data[0]._id);
      }
    } catch (err) {
      toast.error("Error loading sites");
    }
  };

  const loadSubmissions = async () => {
    try {
      const response = await API.get(`/forms/supervisor/${currentSite}`);
      setSubmissions(response.data);
    } catch (err) {
      toast.error("Error loading submissions");
    }
  };

  const downloadPDF = (url, name) => {
    if (!url) {
      toast.error("PDF not available");
      return;
    }

    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    link.click();

    toast.success("Download initiated");
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-6 md:p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold">📋 Submitted Forms</h1>
          <p className="text-muted-foreground mt-2">View the forms filled out by workers</p>
        </div>

        {/* Site Selector */}
        {sites.length > 0 && (
          <div className="mb-6 p-4 bg-card border border-border rounded-lg">
            <label className="block text-sm font-medium mb-2">Select Site</label>
            <select
              value={currentSite || ""}
              onChange={(e) => setCurrentSite(e.target.value)}
              className="w-full p-2 border border-border rounded-lg bg-background"
            >
              {sites.map(site => (
                <option key={site._id} value={site._id}>
                  {site.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Submissions List */}
        <div className="space-y-3">
          {submissions.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-border rounded-lg">
              <p className="text-muted-foreground">No submissions</p>
            </div>
          ) : (
            submissions.map((sub, idx) => (
              <div key={sub._id} className="border border-border rounded-lg overflow-hidden bg-card">
                <div
                  className="p-5 cursor-pointer hover:bg-accent transition flex justify-between items-center"
                  onClick={() => setExpandedSub(expandedSub === sub._id ? null : sub._id)}
                >
                  <div className="flex-1">
                    <h3 className="font-semibold">
                      {sub.form_id?.name || "Form"}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      👤 {sub.worker_id?.full_name} • 
                      {sub.status === "submitted" ? (
                        <span className="text-green-600 ml-1">✓ Submitted</span>
                      ) : (
                        <span className="text-yellow-600 ml-1">✎ Draft</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      📅 {new Date(sub.updated_at).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                  <span className="text-muted-foreground">{expandedSub === sub._id ? "▼" : "▶"}</span>
                </div>

                {expandedSub === sub._id && (
                  <div className="border-t border-border p-5 bg-accent/30 space-y-3">
                    {sub.pdf_filled_url && (
                      <button
                        onClick={() => downloadPDF(sub.pdf_filled_url, `${sub.form_id?.name}-filled.pdf`)}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg flex items-center justify-center gap-2 font-medium transition"
                      >
                        <Download size={16} /> Download Completed PDF
                      </button>
                    )}

                    {sub.submission_data && (
                      <div className="bg-background border border-border rounded-lg p-4">
                        <h4 className="font-semibold mb-2">Form Data</h4>
                        <pre className="text-xs overflow-auto max-h-48">
                          {JSON.stringify(sub.submission_data, null, 2)}
                        </pre>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                      Status: <strong>{sub.status === "submitted" ? "Submitted" : "Draft"}</strong>
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}